import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PlayCircle, User, ArrowRight, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CourseThumbnail } from "@/components/CourseThumbnail";
import { StarRating } from "@/components/StarRating";

export default function CreatorPublicProfilePage() {
  const { handle } = useParams();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/creator/public/${handle}`)
      .then((res) => setCreator(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [handle]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="creator-profile-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !creator) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="creator-profile-not-found">
        <XCircle className="w-10 h-10 text-slate-500 mx-auto mb-3" />
        <h1 className="font-heading text-xl font-semibold text-white">Creator not found</h1>
        <p className="text-sm text-slate-400 mt-2">This creator handle doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="creator-public-profile-page">
      <div className="glass-panel rounded-2xl p-6 sm:p-8 flex items-center gap-5 mb-8">
        <Avatar className="h-20 w-20 border-2 border-white/10">
          <AvatarImage src={creator.picture} alt={creator.name} />
          <AvatarFallback className="bg-primary/20 text-white text-2xl">{creator.name?.[0]?.toUpperCase() || "C"}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white" data-testid="creator-profile-name">{creator.name}</h1>
          <p className="text-sm text-pink-300 font-mono">@{creator.handle}</p>
          {creator.bio && <p className="text-sm text-slate-400 mt-2 max-w-xl" data-testid="creator-profile-bio">{creator.bio}</p>}
        </div>
      </div>

      <h2 className="font-heading text-lg font-semibold text-white mb-4">Courses ({creator.courses.length})</h2>
      {creator.courses.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center" data-testid="creator-profile-no-courses">
          <PlayCircle className="w-8 h-8 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No published courses yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {creator.courses.map((c) => (
            <Link key={c.id} to={`/courses/${c.id}`} data-testid={`creator-profile-course-card-${c.id}`} className="glass-panel rounded-2xl p-5 flex flex-col group hover:-translate-y-1 transition-transform">
              <CourseThumbnail url={c.thumbnail_url} className="mb-4" />
              <h3 className="font-heading text-base font-semibold text-white leading-snug group-hover:text-pink-300 transition-colors">{c.title}</h3>
              <p className="text-xs text-slate-400 mt-1.5 line-clamp-2 flex-1">{c.description}</p>
              {c.review_count > 0 && (
                <div className="flex items-center gap-1.5 mt-2">
                  <StarRating rating={c.avg_rating} />
                  <span className="text-[11px] text-slate-500">{c.avg_rating} ({c.review_count})</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm font-bold text-amber-300">{c.price > 0 ? `₹${c.price.toLocaleString("en-IN")}` : "Free"}</span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-primary/80 group-hover:bg-primary px-3 py-1.5 rounded-lg transition-colors">
                  View <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
